import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Message, Conversation, AIModel } from '../types/chat';
import type { JournalEntry } from '../types/accounting';
import { AI_MODELS } from '../types/chat';
import { chatApi, type ChatMessageRequest } from '../services/api/endpoints/chat';
import { healthApi } from '../services/api/endpoints/health';
import { 
  ChatStreamService, 
  type StreamChunkEvent, 
  type ConnectionState as StreamConnectionState,
  generateUUID,
  isValidUUIDv4
} from '../services/api/streaming/ChatStreamService';
import {
  type EnrichedStreamError,
  type SubscriptionErrorMetadata,
  parseSubscriptionError,
  isSubscriptionError
} from '../types/subscription-errors';

type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting' | 'error';

// Mode d'affichage du chat
export type ChatDisplayMode = 'hidden' | 'floating' | 'panel' | 'fullscreen';

interface ChatStore {
  // État d'affichage
  displayMode: ChatDisplayMode;
  panelWidth: number; // Largeur du panel en pourcentage (20-80)
  
  isTyping: boolean;
  isRecording: boolean;
  isLoading: boolean;
  isStreaming: boolean;
  streamingMessageId: string | null;
  streamingContent: string;
  useStreaming: boolean;
  connectionStatus: ConnectionStatus;
  streamConnectionStatus: StreamConnectionState;
  lastError: string | null;
  subscriptionError: EnrichedStreamError | null; // Erreur d'abonnement enrichie
  retryCount: number;
  writeMode: boolean;
  lastProposedEntry: JournalEntry | null;
  conversations: Conversation[];
  activeConversationId: string | null;
  selectedModel: AIModel;
  onJournalEntryProposed?: (entry: JournalEntry) => void;
  
  // Actions d'affichage
  setDisplayMode: (mode: ChatDisplayMode) => void;
  setPanelWidth: (width: number) => void;
  openChat: () => void;
  closeChat: () => void;
  togglePanel: () => void;
  expandToFullscreen: () => void;
  collapseToPanel: () => void;
  
  // Anciennes actions (rétrocompatibilité)
  isFloating: boolean;
  isOpen: boolean;
  setFloating: (floating: boolean) => void;
  setOpen: (open: boolean) => void;
  
  setTyping: (typing: boolean) => void;
  setRecording: (recording: boolean) => void;
  setSelectedModel: (model: AIModel) => void;
  setWriteMode: (active: boolean) => void;
  toggleWriteMode: () => void;
  toggleStreaming: () => void;
  clearLastProposedEntry: () => void;
  clearError: () => void;
  setOnJournalEntryProposed: (callback: (entry: JournalEntry) => void) => void;
  
  createNewConversation: () => string;
  deleteConversation: (id: string) => Promise<void>;
  setActiveConversation: (id: string) => void;
  fetchConversations: () => Promise<void>;
  fetchConversationHistory: (id: string) => Promise<void>;
  checkBackendHealth: () => Promise<boolean>;
  connectStreaming: (authToken?: string) => Promise<void>;
  disconnectStreaming: () => void;
  cancelCurrentStream: () => boolean;  // Nouveau: Annuler le stream en cours
  
  sendMessage: (content: string, attachment?: Message['attachment']) => Promise<void>;
  sendMessageWithStreaming: (content: string, attachment?: Message['attachment']) => Promise<void>;
  addMessage: (content: string, type: 'user' | 'bot', attachment?: Message['attachment']) => Promise<void>;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  updateStreamingContent: (content: string) => void;
  toggleLike: (messageId: string) => void;
  toggleDislike: (messageId: string) => void;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

/**
 * Sanitize message content to ensure it's always a valid string.
 * Handles arrays (corrupted localStorage data), null, undefined, and other types.
 */
const sanitizeContent = (content: unknown): string => {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    // Extract only strings from the array, ignore booleans and other types
    return content.filter((item): item is string => typeof item === 'string').join('');
  }
  if (content === null || content === undefined) return '';
  return String(content);
};

/**
 * Vérifie si un ID est un ID local (ancien format) ou un UUID valide
 * Les IDs locaux commencent par 'local-' et ne sont pas des UUIDs
 */
const isLocalId = (id: string): boolean => {
  // Si ça commence par 'local-', c'est un ancien ID local
  if (id.startsWith('local-')) return true;
  // Si ce n'est pas un UUID v4 valide, on le considère comme local
  return !isValidUUIDv4(id);
};

/**
 * Crée une nouvelle conversation avec un UUID v4 généré côté client
 * Cet ID sera accepté par le backend pour éviter la race condition du streaming
 * 
 * @see API DOCUMENTATION/chat.md - Section "Client-Generated conversationId"
 */
const createLocalConversation = (model: AIModel): Conversation => {
  // 🆕 v2.5.0: Générer un UUID v4 valide côté client
  // Le backend acceptera cet ID lors de la création de la conversation
  const conversationId = generateUUID();
  
  console.log('🆕 Nouvelle conversation créée avec UUID client:', conversationId);
  
  return {
    id: conversationId, // UUID v4 valide (pas 'local-xxx')
    title: 'Nouvelle conversation',
    timestamp: new Date().toISOString(),
    messages: [],
    isActive: true,
    model,
    context: []
  };
};

// Créer la conversation initiale avec un ID stable
const initialConversation = createLocalConversation(AI_MODELS[0]);

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      // Nouveau système d'affichage
      displayMode: 'hidden' as ChatDisplayMode,
      panelWidth: 40,
      
      // Rétrocompatibilité (computed)
      get isFloating() { return get().displayMode === 'floating'; },
      get isOpen() { return get().displayMode !== 'hidden'; },
      
      isTyping: false,
      isRecording: false,
      isLoading: false,
      isStreaming: false,
      streamingMessageId: null,
      streamingContent: '',
      useStreaming: ChatStreamService.isSupported(),
      connectionStatus: navigator.onLine ? 'connected' : 'disconnected',
      streamConnectionStatus: 'disconnected',
      lastError: null,
      subscriptionError: null,
      retryCount: 0,
      writeMode: false,
      lastProposedEntry: null,
      selectedModel: AI_MODELS[0],
      conversations: [initialConversation],
      activeConversationId: initialConversation.id,
      onJournalEntryProposed: undefined,

      // Nouvelles actions d'affichage
      setDisplayMode: (mode) => set({ displayMode: mode }),
      setPanelWidth: (width) => set({ panelWidth: Math.min(80, Math.max(20, width)) }),
      openChat: () => set({ displayMode: 'panel' }),
      closeChat: () => set({ displayMode: 'hidden' }),
      togglePanel: () => set((state) => ({ 
        displayMode: state.displayMode === 'hidden' ? 'panel' : 'hidden' 
      })),
      expandToFullscreen: () => set({ displayMode: 'fullscreen' }),
      collapseToPanel: () => set({ displayMode: 'panel' }),

      // Rétrocompatibilité
      setFloating: (floating) => set({ displayMode: floating ? 'floating' : 'panel' }),
      setOpen: (open) => set({ displayMode: open ? 'panel' : 'hidden', isOpen: open }),
      setTyping: (typing) => set({ isTyping: typing }),
      setRecording: (recording) => set({ isRecording: recording }),
      setSelectedModel: (model) => set({ selectedModel: model }),
      setWriteMode: (active) => set({ writeMode: active }),
      toggleWriteMode: () => set((state) => ({ writeMode: !state.writeMode })),
      toggleStreaming: () => set((state) => ({ useStreaming: !state.useStreaming })),
      clearLastProposedEntry: () => set({ lastProposedEntry: null }),
      clearError: () => set({ lastError: null, subscriptionError: null, connectionStatus: 'connected' }),
      setOnJournalEntryProposed: (callback) => set({ onJournalEntryProposed: callback }),
      updateStreamingContent: (content) => set({ streamingContent: content }),

      createNewConversation: () => {
        const store = get();
        const newConversation = createLocalConversation(store.selectedModel);
        set((state) => ({
          conversations: [...state.conversations, newConversation],
          activeConversationId: newConversation.id
        }));
        return newConversation.id;
      },

      deleteConversation: async (id) => {
        set((state) => {
          const newConversations = state.conversations.filter(c => c.id !== id);
          const newActiveId = state.activeConversationId === id 
            ? newConversations[0]?.id || null 
            : state.activeConversationId;
          return {
            conversations: newConversations.length > 0 
              ? newConversations 
              : [createLocalConversation(state.selectedModel)],
            activeConversationId: newActiveId || newConversations[0]?.id || null
          };
        });
        
        if (!isLocalId(id) && navigator.onLine) {
          try {
            await chatApi.deleteConversation(id);
          } catch (error) {
            console.warn('Échec suppression serveur:', error);
          }
        }
      },

      setActiveConversation: (id) => {
        set({ activeConversationId: id });
        if (!isLocalId(id)) {
          get().fetchConversationHistory(id);
        }
      },

      fetchConversations: async () => {
        if (!navigator.onLine) {
          set({ connectionStatus: 'disconnected' });
          return;
        }

        set({ isLoading: true, lastError: null });
        
        try {
          const response = await chatApi.getConversations();
          
          if (response.success && response.data?.conversations) {
            const backendConversations = response.data.conversations;
            
            if (backendConversations.length > 0) {
              const localConversations = get().conversations.filter(c => isLocalId(c.id));
              set({ 
                conversations: [...backendConversations, ...localConversations],
                activeConversationId: backendConversations[0]?.id || localConversations[0]?.id || null,
                connectionStatus: 'connected',
                retryCount: 0
              });
            }
          } else {
            throw new Error(response.error || 'Erreur récupération conversations');
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erreur de connexion';
          console.error('Erreur fetchConversations:', errorMessage);
          set({ 
            connectionStatus: 'error',
            lastError: `Impossible de charger les conversations: ${errorMessage}`
          });
        } finally {
          set({ isLoading: false });
        }
      },

      fetchConversationHistory: async (id) => {
        if (!navigator.onLine || isLocalId(id)) return;

        try {
          const response = await chatApi.getConversationById(id);
          if (response.success && response.data) {
            set((state) => ({
              conversations: state.conversations.map(conv =>
                conv.id === id ? { ...conv, ...response.data } : conv
              )
            }));
          }
        } catch (error) {
          console.warn('Impossible de charger l\'historique:', error);
        }
      },

      checkBackendHealth: async () => {
        try {
          const result = await healthApi.check();
          
          if (result.success && result.data?.status === 'healthy') {
            set({ connectionStatus: 'connected', lastError: null });
            return true;
          } else {
            set({ 
              connectionStatus: 'error',
              lastError: result.error || 'Backend indisponible'
            });
            return false;
          }
        } catch (error) {
          set({ 
            connectionStatus: 'error',
            lastError: 'Impossible de joindre le serveur'
          });
          return false;
        }
      },

      connectStreaming: async (authToken?: string) => {
        if (!ChatStreamService.isSupported()) {
          console.warn('Socket.IO non supporté, utilisation du mode HTTP');
          set({ useStreaming: false });
          return;
        }

        // Définir le token d'authentification si fourni
        if (authToken) {
          ChatStreamService.setAuthToken(authToken);
        }

        try {
          await ChatStreamService.connect({
            onChunk: (chunk: StreamChunkEvent) => {
              const store = get();
              
              console.log('📨 onChunk callback appelé:', {
                chunkId: chunk.chunkId,
                contentLength: chunk.content?.length,
                streamingMessageId: store.streamingMessageId,
                activeConversationId: store.activeConversationId
              });

              // Mettre à jour le contenu en streaming
              set({ 
                streamingContent: chunk.content,
                isStreaming: true 
              });
              
              // Mettre à jour le message en cours dans la conversation
              if (store.streamingMessageId) {
                set((state) => ({
                  conversations: state.conversations.map(conv => 
                    conv.id === state.activeConversationId
                      ? {
                          ...conv,
                          messages: conv.messages.map(msg =>
                            msg.id === store.streamingMessageId 
                              ? { ...msg, content: chunk.content }
                              : msg
                          )
                        }
                      : conv
                  )
                }));
              } else {
                console.warn('⚠️ Chunk reçu mais pas de streamingMessageId défini!');
              }
            },
            
            onEnd: (event: StreamChunkEvent) => {
              const store = get();
              
              console.log('✅ onEnd callback appelé:', {
                contentLength: event.content?.length,
                hasJournalEntry: !!event.journalEntry,
                streamingMessageId: store.streamingMessageId,
                processingDetails: event.processingDetails
              });

              // Finaliser le message
              if (store.streamingMessageId) {
                set((state) => ({
                  conversations: state.conversations.map(conv => {
                    if (conv.id === state.activeConversationId) {
                      // Mettre à jour l'ID de conversation si c'était local
                      const newConvId = event.conversationId || conv.id;
                      return {
                        ...conv,
                        id: newConvId,
                        messages: conv.messages.map(msg =>
                          msg.id === store.streamingMessageId 
                            ? { ...msg, id: event.id, content: event.content }
                            : msg
                        )
                      };
                    }
                    return conv;
                  }),
                  activeConversationId: event.conversationId || state.activeConversationId
                }));
              } else {
                console.warn('⚠️ Stream end reçu mais pas de streamingMessageId!');
              }
              
              // Gérer l'écriture comptable si présente
              if (event.journalEntry) {
                set({ lastProposedEntry: event.journalEntry });
                const { onJournalEntryProposed } = get();
                if (onJournalEntryProposed) {
                  onJournalEntryProposed(event.journalEntry);
                }
              }

              // Log des détails de traitement (pour debug/analytics)
              if (event.processingDetails) {
                console.log('📊 Streaming terminé:', {
                  chunks: event.processingDetails.totalChunks,
                  tokens: event.processingDetails.tokensUsed,
                  time: event.processingDetails.processingTime,
                  model: event.processingDetails.aiModel
                });
              }

              // Gérer les actions suggérées si présentes
              if (event.suggestedActions && event.suggestedActions.length > 0) {
                console.log('💡 Actions suggérées:', event.suggestedActions);
                // TODO: Implémenter la gestion des suggestedActions dans l'UI
              }
              
              set({ 
                isStreaming: false, 
                isTyping: false,
                streamingMessageId: null,
                streamingContent: ''
              });
            },
            
            onError: (error) => {
              console.error('Erreur streaming:', error);
              
              // Extraire le message et les métadonnées
              let errorMessage: string;
              let enrichedError: EnrichedStreamError | null = null;
              
              if (error instanceof Error) {
                errorMessage = error.message;
              } else {
                const streamError = error as StreamChunkEvent;
                errorMessage = streamError.content || 'Erreur de streaming';
                
                // Parser les métadonnées pour détecter les erreurs d'abonnement
                if (streamError.metadata) {
                  enrichedError = parseSubscriptionError(errorMessage, streamError.metadata);
                  
                  if (enrichedError.isSubscriptionError) {
                    console.warn('⚠️ Erreur d\'abonnement détectée:', enrichedError.subscriptionError);
                  }
                }
              }
              
              set({ 
                isStreaming: false, 
                isTyping: false,
                streamingMessageId: null,
                streamingContent: '',
                lastError: errorMessage,
                subscriptionError: enrichedError
              });
            },

            // Callback pour les appels d'outils IA (ex: calcul, recherche)
            onToolCall: (event: StreamChunkEvent) => {
              console.log('🔧 Outil IA appelé:', event.content);
              // Optionnel: Afficher un indicateur dans l'UI
            },

            // Callback pour les résultats d'outils IA
            onToolResult: (event: StreamChunkEvent) => {
              console.log('🔧 Résultat outil:', event.content);
              // Le résultat sera intégré dans le contenu final
            },

            // Callback pour stream annulé (v2.4.0)
            onCancelled: (event: StreamChunkEvent) => {
              const store = get();
              console.log('🛑 Stream annulé:', event.content);
              
              // Mettre à jour le message avec indication d'annulation
              if (store.streamingMessageId) {
                const currentContent = store.streamingContent || '';
                set((state) => ({
                  conversations: state.conversations.map(conv => 
                    conv.id === state.activeConversationId
                      ? {
                          ...conv,
                          messages: conv.messages.map(msg =>
                            msg.id === store.streamingMessageId 
                              ? { ...msg, content: currentContent + '\n\n*[Génération arrêtée]*' }
                              : msg
                          )
                        }
                      : conv
                  ),
                  isStreaming: false,
                  isTyping: false,
                  streamingMessageId: null,
                  streamingContent: ''
                }));
              }
            },

            // Callback heartbeat (v2.4.0)
            onHeartbeat: () => {
              // Heartbeat reçu - la connexion est active
              // Optionnel: mettre à jour un timestamp pour détection de déconnexion
            },

            onConnectionChange: (state) => {
              set({ streamConnectionStatus: state });
              console.log('📡 État connexion streaming:', state);
            }
          });
          
          console.log('✅ Streaming Socket.IO connecté');
          set({ streamConnectionStatus: 'connected' });
        } catch (error) {
          console.error('Échec connexion streaming:', error);
          set({ useStreaming: false, streamConnectionStatus: 'error' });
        }
      },

      disconnectStreaming: () => {
        ChatStreamService.disconnect();
        set({ 
          isStreaming: false, 
          streamingMessageId: null,
          streamingContent: '',
          streamConnectionStatus: 'disconnected'
        });
      },

      /**
       * Annule le stream en cours (v2.4.0)
       * Envoie cancel_stream au serveur et met à jour l'état local
       */
      cancelCurrentStream: () => {
        const store = get();
        
        if (!store.isStreaming || !store.streamingMessageId) {
          console.warn('⚠️ Aucun stream en cours à annuler');
          return false;
        }

        console.log('🛑 Annulation du stream:', store.streamingMessageId);
        
        // Appeler le service pour envoyer l'événement WebSocket
        const cancelled = ChatStreamService.cancelStream(store.streamingMessageId);
        
        if (cancelled) {
          // Mettre à jour l'état local immédiatement (optimistic update)
          const currentContent = store.streamingContent || '';
          set((state) => ({
            conversations: state.conversations.map(conv => 
              conv.id === state.activeConversationId
                ? {
                    ...conv,
                    messages: conv.messages.map(msg =>
                      msg.id === store.streamingMessageId 
                        ? { ...msg, content: currentContent + (currentContent ? '\n\n' : '') + '*[Génération arrêtée]*' }
                        : msg
                    )
                  }
                : conv
            ),
            isStreaming: false,
            isTyping: false,
            streamingMessageId: null,
            streamingContent: ''
          }));
        }
        
        return cancelled;
      },

      addMessage: async (content, type, attachment) => {
        // Validation: s'assurer que le contenu est une chaîne valide
        const safeContent = sanitizeContent(content);
        
        if (type === 'user') {
          const store = get();
          
          console.log('💬 addMessage appelé', {
            useStreaming: store.useStreaming,
            isStreamingSupported: ChatStreamService.isSupported(),
            isStreamConnected: ChatStreamService.isConnected(),
            streamConnectionStatus: store.streamConnectionStatus
          });

          // TOUJOURS privilégier le streaming pour éviter les timeouts
          if (ChatStreamService.isSupported()) {
            // Tenter de se connecter si pas connecté
            if (!ChatStreamService.isConnected()) {
              console.log('🔌 Tentative de connexion au streaming...');
              try {
                await get().connectStreaming();
                console.log('✅ Connexion streaming réussie');
              } catch (error) {
                console.warn('⚠️ Connexion streaming échouée:', error);
              }
            }
            
            // Si connecté après tentative, utiliser le streaming
            if (ChatStreamService.isConnected()) {
              console.log('🚀 Utilisation du mode STREAMING');
              await get().sendMessageWithStreaming(safeContent, attachment);
              return;
            } else {
              console.warn('⚠️ WebSocket non connecté après tentative, fallback HTTP');
            }
          }
          
          // Fallback: mode HTTP (peut timeout, mais on essaie quand même)
          // Note: Le backend devrait avoir POST /chat/stream même sans WebSocket
          console.log('📡 Utilisation du mode HTTP (fallback) - /chat endpoint');
          await get().sendMessage(safeContent, attachment);
        }
      },

      sendMessage: async (content, attachment) => {
        // Validation: s'assurer que le contenu est une chaîne valide
        const safeContent = sanitizeContent(content);
        
        const store = get();
        let activeConversation = store.conversations.find(
          c => c.id === store.activeConversationId
        );

        if (!activeConversation) {
          const newId = get().createNewConversation();
          activeConversation = get().conversations.find(c => c.id === newId);
          if (!activeConversation) return;
        }

        const currentLocalId = activeConversation.id;
        const isLocal = isLocalId(currentLocalId);

        const userMessage: Message = {
          id: `local-msg-${Date.now()}`,
          sender: 'user',
          content: safeContent,
          timestamp: new Date().toISOString(),
          likes: 0,
          dislikes: 0,
          attachment
        };

        set((state) => ({
          conversations: state.conversations.map(conv => 
            conv.id === currentLocalId
              ? { ...conv, messages: [...conv.messages, userMessage] }
              : conv
          )
        }));

        if (!navigator.onLine) {
          const offlineMessage: Message = {
            id: `local-msg-${Date.now()}-offline`,
            sender: 'bot',
            content: '⚠️ **Hors ligne**\n\nReconnectez-vous pour envoyer votre message.',
            timestamp: new Date().toISOString(),
            likes: 0,
            dislikes: 0
          };
          set((state) => ({
            connectionStatus: 'disconnected',
            lastError: 'Connexion internet indisponible',
            conversations: state.conversations.map(conv => 
              conv.id === currentLocalId
                ? { ...conv, messages: [...conv.messages, offlineMessage] }
                : conv
            )
          }));
          return;
        }

        // 🆕 v2.5.0: Toujours envoyer le conversationId (UUID client-generated)
        // ✅ v2.6.0: L'historique est maintenant géré côté backend
        const request: ChatMessageRequest = {
          conversationId: activeConversation.id, // ✅ Toujours un UUID valide
          message: { content: safeContent, attachment },
          modelId: store.selectedModel.id,
          writeMode: store.writeMode,
          context: activeConversation.context
        };

        set({ isTyping: true, lastError: null, connectionStatus: 'connected' });

        let retryAttempt = 0;
        
        while (retryAttempt <= MAX_RETRIES) {
          try {
            const response = await chatApi.sendMessage(request);
            
            if (response.success && response.data) {
              // Le backend peut renvoyer soit:
              // 1. { message: {...}, conversationId: "..." } (format documenté)
              // 2. { id, sender, content, timestamp, conversationId } (format actuel backend)
              const data = response.data as any;
              
              // Détecter le format de réponse
              const botMessage = data.message || data; // Si pas de .message, c'est le message directement
              const conversationId = data.conversationId || botMessage.conversationId;
              const journalEntry = data.journalEntry;

              const newBotMessage: Message = {
                id: botMessage.id,
                sender: botMessage.sender || 'bot',
                content: botMessage.content,
                timestamp: botMessage.timestamp || new Date().toISOString(),
                likes: botMessage.likes || 0,
                dislikes: botMessage.dislikes || 0
              };

              set((state) => ({
                conversations: state.conversations.map(conv => {
                  if (conv.id === currentLocalId) {
                    return {
                      ...conv,
                      id: conversationId,
                      messages: [...conv.messages, newBotMessage]
                    };
                  }
                  return conv;
                }),
                activeConversationId: conversationId,
                retryCount: 0,
                connectionStatus: 'connected',
                isTyping: false
              }));

              if (journalEntry) {
                set({ lastProposedEntry: journalEntry });
                const { onJournalEntryProposed } = get();
                if (onJournalEntryProposed) {
                  onJournalEntryProposed(journalEntry);
                }
              }
              return;
            } else {
              throw new Error(response.error || 'Réponse invalide');
            }
          } catch (error) {
            retryAttempt++;
            
            if (retryAttempt <= MAX_RETRIES && navigator.onLine) {
              set({ 
                connectionStatus: 'reconnecting',
                retryCount: retryAttempt,
                lastError: `Tentative ${retryAttempt}/${MAX_RETRIES}...`
              });
              await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
            } else {
              const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
              
              set({ 
                connectionStatus: 'error',
                lastError: `Service ADHA indisponible: ${errorMessage}`,
                retryCount: 0,
                isTyping: false
              });

              const errorBotMessage: Message = {
                id: `local-msg-${Date.now()}-error`,
                sender: 'bot',
                content: `⚠️ **Erreur**\n\nImpossible de contacter ADHA.\n\n*${errorMessage}*\n\n💡 Vérifiez votre connexion et réessayez.`,
                timestamp: new Date().toISOString(),
                likes: 0,
                dislikes: 0
              };

              set((state) => ({
                conversations: state.conversations.map(conv => 
                  conv.id === currentLocalId || conv.id === state.activeConversationId
                    ? { ...conv, messages: [...conv.messages, errorBotMessage] }
                    : conv
                )
              }));
              return;
            }
          }
        }
      },

      sendMessageWithStreaming: async (content, attachment) => {
        // Validation: s'assurer que le contenu est une chaîne valide
        const safeContent = sanitizeContent(content);
        
        const store = get();
        let activeConversation = store.conversations.find(
          c => c.id === store.activeConversationId
        );

        console.log('🚀 sendMessageWithStreaming démarré', {
          activeConversationId: store.activeConversationId,
          hasActiveConversation: !!activeConversation,
          writeMode: store.writeMode
        });

        if (!activeConversation) {
          const newId = get().createNewConversation();
          activeConversation = get().conversations.find(c => c.id === newId);
          if (!activeConversation) {
            console.error('❌ Impossible de créer une nouvelle conversation');
            return;
          }
          console.log('📝 Nouvelle conversation créée:', newId);
        }

        const currentLocalId = activeConversation.id;
        const isLocal = isLocalId(currentLocalId);

        console.log('📋 Conversation active:', {
          id: currentLocalId,
          isLocal,
          messagesCount: activeConversation.messages.length
        });

        // Ajouter le message utilisateur
        const userMessage: Message = {
          id: `local-msg-${Date.now()}`,
          sender: 'user',
          content: safeContent,
          timestamp: new Date().toISOString(),
          likes: 0,
          dislikes: 0,
          attachment
        };

        // Créer un message bot vide pour le streaming
        const streamingMsgId = `streaming-msg-${Date.now()}`;
        const botPlaceholder: Message = {
          id: streamingMsgId,
          sender: 'bot',
          content: '',
          timestamp: new Date().toISOString(),
          likes: 0,
          dislikes: 0
        };

        set((state) => ({
          conversations: state.conversations.map(conv => 
            conv.id === currentLocalId
              ? { ...conv, messages: [...conv.messages, userMessage, botPlaceholder] }
              : conv
          ),
          isTyping: true,
          isStreaming: true,
          streamingMessageId: streamingMsgId,
          streamingContent: '',
          lastError: null
        }));

        console.log('📝 Messages ajoutés à la conversation:', {
          userMessageId: userMessage.id,
          streamingMsgId,
          isStreaming: true
        });

        // Vérifier la connexion Socket.IO
        if (!ChatStreamService.isConnected()) {
          console.log('⚠️ Socket non connecté, tentative de connexion...');
          try {
            await get().connectStreaming();
            console.log('✅ Reconnexion Socket réussie');
          } catch (error) {
            console.error('❌ Impossible de connecter au streaming, fallback HTTP:', error);
            // Supprimer le placeholder et utiliser le mode HTTP
            set((state) => ({
              conversations: state.conversations.map(conv => 
                conv.id === currentLocalId
                  ? { ...conv, messages: conv.messages.filter(m => m.id !== streamingMsgId) }
                  : conv
              ),
              isStreaming: false,
              streamingMessageId: null
            }));
            // Fallback au mode HTTP
            console.log('📡 Fallback vers mode HTTP /chat');
            await get().sendMessage(safeContent, attachment);
            return;
          }
        }

        // Envoyer le message via l'endpoint REST /chat/stream
        // 🆕 v2.5.0: Toujours envoyer le conversationId (UUID client-generated)
        // Le backend accepte maintenant les UUIDs générés côté client
        console.log('📤 Appel ChatStreamService.sendStreamMessage v2.5.0...', {
          conversationId: activeConversation.id,
          isClientGenerated: isLocal,
          writeMode: store.writeMode
        });

        try {
          // 🆕 v2.5.0: Toujours envoyer le conversationId (UUID valide)
          // ✅ v2.6.0: L'historique est maintenant géré côté backend
          // ChatStreamService.sendStreamMessage gère:
          // 1. La souscription WebSocket AVANT l'appel HTTP
          // 2. L'envoi du conversationId au backend
          // → Pas de race condition, streaming complet dès le 1er message!
          const result = await ChatStreamService.sendStreamMessage({
            conversationId: activeConversation.id, // ✅ Toujours un UUID valide
            content: safeContent,
            attachment,
            writeMode: store.writeMode,
            modelId: store.selectedModel.id,
            context: activeConversation.context
          });

          console.log('✅ sendStreamMessage retourné:', result);

          // Mettre à jour les IDs (conversation et message utilisateur)
          set((state) => ({
            conversations: state.conversations.map(conv => {
              if (conv.id === currentLocalId) {
                return {
                  ...conv,
                  // Mettre à jour l'ID de conversation si nouvelle
                  id: result.conversationId || conv.id,
                  // Mettre à jour l'ID du message utilisateur avec celui du backend
                  messages: conv.messages.map(msg =>
                    msg.id === userMessage.id && result.userMessageId
                      ? { ...msg, id: result.userMessageId }
                      : msg
                  )
                };
              }
              return conv;
            }),
            activeConversationId: result.conversationId || state.activeConversationId
          }));

          // Le reste est géré par les callbacks Socket.IO (onChunk, onEnd, onError)
        } catch (error) {
          console.error('Erreur envoi streaming:', error);
          const errorMessage = error instanceof Error ? error.message : 'Erreur de streaming';
          
          // Supprimer le placeholder et afficher erreur
          set((state) => ({
            conversations: state.conversations.map(conv => 
              conv.id === currentLocalId || conv.id === state.activeConversationId
                ? { 
                    ...conv, 
                    messages: conv.messages.map(m => 
                      m.id === streamingMsgId 
                        ? { ...m, content: `⚠️ **Erreur**\n\n${errorMessage}\n\n💡 Réessayez ou passez en mode HTTP.` }
                        : m
                    )
                  }
                : conv
            ),
            isStreaming: false,
            isTyping: false,
            streamingMessageId: null,
            lastError: errorMessage
          }));
        }
      },

      updateMessage: (messageId, updates) => {
        // Sanitize content if it's being updated
        const sanitizedUpdates = updates.content !== undefined 
          ? { ...updates, content: sanitizeContent(updates.content) }
          : updates;
        
        set((state) => ({
          conversations: state.conversations.map(conv => 
            conv.id === state.activeConversationId
              ? {
                  ...conv,
                  messages: conv.messages.map(msg =>
                    msg.id === messageId ? { ...msg, ...sanitizedUpdates } : msg
                  )
                }
              : conv
          )
        }));
      },

      toggleLike: (messageId) => {
        set((state) => ({
          conversations: state.conversations.map(conv => 
            conv.id === state.activeConversationId
              ? {
                  ...conv,
                  messages: conv.messages.map(msg =>
                    msg.id === messageId ? { ...msg, likes: (msg.likes || 0) + 1 } : msg
                  )
                }
              : conv
          )
        }));
      },

      toggleDislike: (messageId) => {
        set((state) => ({
          conversations: state.conversations.map(conv => 
            conv.id === state.activeConversationId
              ? {
                  ...conv,
                  messages: conv.messages.map(msg =>
                    msg.id === messageId ? { ...msg, dislikes: (msg.dislikes || 0) + 1 } : msg
                  )
                }
              : conv
          )
        }));
      }
    }),
    {
      name: 'chat-storage',
      partialize: (state) => ({
        conversations: state.conversations,
        activeConversationId: state.activeConversationId,
        writeMode: state.writeMode,
        isFloating: state.isFloating,
        useStreaming: state.useStreaming
      }),
      // Migration pour nettoyer les données corrompues
      migrate: (persistedState: any, version: number) => {
        if (persistedState?.conversations) {
          // S'assurer que tous les messages ont un contenu string valide
          persistedState.conversations = persistedState.conversations.map((conv: any) => ({
            ...conv,
            messages: (conv.messages || []).map((msg: any) => {
              let safeContent: string;
              const c = msg.content;
              if (typeof c === 'string') {
                safeContent = c;
              } else if (Array.isArray(c)) {
                // Extraire uniquement les strings de l'array
                safeContent = c.filter((item: any): item is string => typeof item === 'string').join('');
              } else if (c === null || c === undefined) {
                safeContent = '';
              } else {
                safeContent = String(c);
              }
              return { ...msg, content: safeContent };
            })
          }));
        }
        return persistedState;
      },
      version: 2, // Incrementé pour forcer la migration et nettoyer les données corrompues
    }
  )
);

if (typeof window !== 'undefined') {
  window.addEventListener('online', async () => {
    useChatStore.setState({ connectionStatus: 'reconnecting', lastError: null });
    // Vérifier la santé du backend avant de charger les conversations
    const isHealthy = await useChatStore.getState().checkBackendHealth();
    if (isHealthy) {
      useChatStore.getState().fetchConversations();
    }
  });

  window.addEventListener('offline', () => {
    useChatStore.setState({ 
      connectionStatus: 'disconnected',
      lastError: 'Connexion internet perdue'
    });
  });

  // Health check initial au chargement
  setTimeout(async () => {
    if (navigator.onLine) {
      await useChatStore.getState().checkBackendHealth();
    }
  }, 1000);
}
